import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { List, X, SignOut, User, Stethoscope, Briefcase, CaretRight, MapPin, ArrowRight } from "@phosphor-icons/react";
import InvisiqueLogo from "@/components/site/Logo";
import { portalUrl } from "@/lib/host-mode";
import { DesktopProductsMenu, MobileProductsSection } from "@/components/site/MegaMenu";

/* Cross-host portal navigation. In universal mode (preview/local) this
 * returns a relative path so React Router handles it; in production
 * marketing mode it returns an absolute URL to `my.invisique.in` which
 * the browser navigates to as a full page load. */
const goPortal = (path) => { window.location.assign(portalUrl(path)); };

const NAV = [
    { to: "/", label: "Home" },
    { to: "/why-invisique", label: "Why Invisique™" },
    { to: "/how-it-works", label: "How It Works" },
    { to: "/pricing", label: "Pricing" },
];

export const Header = () => {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const portalPath = user?.role === "doctor" ? "/doctor" : "/patient";

    return (
        <header className="sticky top-0 z-50 glass-header border-b border-border/60" data-testid="site-header">
            {/* Utility bar, only shown when logged out. Keeps the main nav uncluttered
                while still giving doctors and clinics a one-click entry into their portal. */}
            {!user && (
                <div className="bg-secondary/40 border-b border-border/60 hidden md:block" data-testid="utility-bar">
                    <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-end gap-2 h-9 text-xs">
                        {/* Patient Portal — ghost pill. Neutral by default,
                            picks up a soft primary tint on hover. Quieter
                            than For Dentists below so the visual hierarchy
                            stays: Find a Doctor (primary) > Provider B2B
                            (medium) > Portal sign-ins (low). */}
                        <button
                            onClick={() => goPortal("/patient-login")}
                            className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-foreground/75 hover:text-primary hover:bg-primary/8 ring-1 ring-transparent hover:ring-primary/20 transition-all"
                            data-testid="utility-patient-signin"
                        >
                            <User size={12} weight="duotone" className="transition-transform group-hover:scale-110" />
                            <span className="font-medium">Patient Portal</span>
                        </button>

                        <button
                            onClick={() => goPortal("/doctor-login")}
                            className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-foreground/75 hover:text-primary hover:bg-primary/8 ring-1 ring-transparent hover:ring-primary/20 transition-all"
                            data-testid="utility-doctor-signin"
                        >
                            <Stethoscope size={12} weight="duotone" className="transition-transform group-hover:scale-110" />
                            <span className="font-medium">Provider Portal</span>
                        </button>

                        {/* B2B funnel entry — slightly stronger weight than
                            the two portals above (soft primary tint by
                            default, deeper on hover) so it reads as the
                            "join the network" pillar without competing
                            with the Find a Doctor primary CTA. */}
                        <button
                            onClick={() => navigate("/become-a-provider")}
                            className="group inline-flex items-center gap-1.5 pl-3 pr-2.5 py-1.5 rounded-full bg-primary/10 text-primary ring-1 ring-primary/20 hover:bg-primary/15 hover:ring-primary/40 transition-all"
                            data-testid="utility-become-provider"
                        >
                            <Briefcase size={12} weight="duotone" />
                            <span className="font-semibold">For Dentists/Orthodontists</span>
                            <CaretRight size={10} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                        </button>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16 lg:h-20">
                <Link to="/" data-testid="home-logo">
                    <InvisiqueLogo />
                </Link>

                <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={`nav-${n.to.replace(/\//g, "-") || "home"}`}
                            className={({ isActive }) =>
                                `group relative text-sm font-medium px-3 py-2 transition-colors whitespace-nowrap rounded-md ${
                                    isActive ? "text-primary" : "text-foreground hover:text-primary"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {n.label}
                                    {/* Animated underline — slides in from the left on hover,
                                        snaps full-width when the route is active. Pure CSS
                                        scaleX with a left transform-origin gives the
                                        directional "draws in" feel polished platforms use. */}
                                    <span
                                        className={`absolute left-3 right-3 -bottom-0.5 h-0.5 bg-primary rounded-full origin-left transition-transform duration-300 ease-out ${
                                            isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                                        }`}
                                        aria-hidden="true"
                                    />
                                </>
                            )}
                        </NavLink>
                    ))}
                    {/* Products mega-menu — Guards & Retainer dropdown. Replaces
                        the flat Infinite Retainers™ link (which is now one of
                        four cards inside the panel). CMS-driven via
                        `nav_products.*` keys. */}
                    <DesktopProductsMenu />
                </nav>

                <div className="hidden lg:flex items-center gap-1 xl:gap-2">
                    {user ? (
                        <>
                            {/* Portal button with inline avatar — patient's
                                uploaded photo or doctor's clinic photo
                                appears as a small disc on the left, falling
                                back to a primary-tinted initial when neither
                                is set. Reinforces identity at a glance
                                without leaving the global header. */}
                            <button
                                onClick={() => navigate(portalPath)}
                                data-testid="header-portal-btn"
                                className="group inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full hover:bg-secondary transition-colors"
                            >
                                {(user.photo_data_url || user.doctor_image) ? (
                                    <img
                                        src={user.photo_data_url || user.doctor_image}
                                        alt={user.name || "You"}
                                        className="w-7 h-7 rounded-full object-cover ring-1 ring-border"
                                        data-testid="header-avatar-img"
                                    />
                                ) : (
                                    <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold grid place-items-center ring-1 ring-primary/20" data-testid="header-avatar-initial">
                                        {(user.doctor_name || user.name || user.phone || "?").slice(0, 1).toUpperCase()}
                                    </span>
                                )}
                                <span className="text-sm font-medium">
                                    {user.role === "doctor" ? "Provider portal" : user.role === "admin" ? "Admin" : "My portal"}
                                </span>
                            </button>
                            <Button variant="outline" onClick={() => { logout(); navigate("/"); }} data-testid="header-logout-btn">
                                <SignOut size={16} className="mr-1" /> Logout
                            </Button>
                        </>
                    ) : (
                        // Premium hero CTA in the header — the highest-intent
                        // action on the whole site lives here. Built as a Link
                        // (not the shadcn Button) so we get full control over
                        // the group-hover motion, the glow ring, and the
                        // micro-arrow slide. Three layers: solid teal pill,
                        // soft primary-tinted shadow ring (lifts on hover),
                        // and an inner shine sweep that runs on hover.
                        <Link
                            to="/find-doctor"
                            data-testid="header-book-btn"
                            className="group relative inline-flex items-center gap-2 pl-4 pr-3.5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 overflow-hidden ring-1 ring-primary/40"
                        >
                            {/* Inner shine — fires once on hover, gives the
                                button a polished, premium feel without
                                feeling gimmicky. Pure CSS, no JS overhead. */}
                            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none" />
                            <MapPin size={16} weight="fill" className="relative shrink-0" />
                            <span className="relative tracking-wide">Find a Doctor</span>
                            <ArrowRight size={14} weight="bold" className="relative shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
                        </Link>
                    )}
                </div>

                <button
                    className="lg:hidden p-2"
                    onClick={() => setOpen(!open)}
                    aria-label="menu"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={24} /> : <List size={24} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-border/60 bg-white" data-testid="mobile-menu">
                    <div className="px-5 py-4 flex flex-col gap-3">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `text-base py-2 ${isActive ? "text-primary font-semibold" : "text-foreground"}`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        {/* Products stacked list — matches the desktop mega-menu. */}
                        <MobileProductsSection onNavigate={() => setOpen(false)} />
                        <div className="border-t border-border pt-3 flex flex-col gap-2">
                            {user ? (
                                <>
                                    <Button onClick={() => { navigate(portalPath); setOpen(false); }}>
                                        {user.role === "doctor" ? "Provider portal" : user.role === "admin" ? "Admin" : "My portal"}
                                    </Button>
                                    <Button variant="outline" onClick={() => { logout(); navigate("/"); setOpen(false); }}>Logout</Button>
                                </>
                            ) : (
                                <>
                                    {/* Mobile mirror of the desktop CTA — same
                                        visual language, full-width because it
                                        lives in a stacked sheet. */}
                                    <Link
                                        to="/find-doctor"
                                        onClick={() => setOpen(false)}
                                        data-testid="mobile-find-doctor-btn"
                                        className="group relative inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary text-primary-foreground text-base font-semibold shadow-lg shadow-primary/25 ring-1 ring-primary/40 transition-all overflow-hidden"
                                    >
                                        <MapPin size={18} weight="fill" />
                                        <span>Find a Doctor</span>
                                        <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                                    </Link>
                                    <div className="grid grid-cols-2 gap-2 mt-1">
                                        <Button variant="outline" size="sm" onClick={() => { setOpen(false); goPortal("/patient-login"); }} data-testid="mobile-patient-signin">
                                            <User size={14} className="mr-1" /> Patient
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => { setOpen(false); goPortal("/doctor-login"); }} data-testid="mobile-doctor-signin">
                                            <Stethoscope size={14} className="mr-1" /> Doctor
                                        </Button>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={() => { navigate("/become-a-provider"); setOpen(false); }} className="text-primary" data-testid="mobile-become-provider">
                                        <Briefcase size={14} className="mr-1" /> For Dentists/Orthodontists
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
